import { SdkContext } from '../internal'
import { noteRowFrom, versionedRowWrite } from '@/lib/realtime/rowVersion'

/**
 * F-108 Mensagens: 1:1 chat between members of the community of
 * `X-CommunityId`. Every route acts only on the caller's own conversations.
 * The client is tolerant on purpose: optional fields may be missing, and a
 * list may come as an array or as `{ items, pagination }`.
 */

export type ChatWhoCanStart = 'EVERYONE' | 'PAID_MEMBERS' | 'TEAM'
export type ChatPanelState = 'COLLAPSED' | 'EXPANDED' | 'SIDE'
/** `FREE`: anyone allowed by `whoCanStart` chats; `CONNECTION` ("Precisa adicionar"): an accepted connection first. */
export type ChatStartMode = 'FREE' | 'CONNECTION'
/** How the caller sees the pair. */
export type ChatConnectionState = 'none' | 'pending_out' | 'pending_in' | 'connected'

export interface ChatConnection {
  id: string
  connection: ChatConnectionState
  status?: 'PENDING' | 'ACCEPTED'
  direction?: 'outgoing' | 'incoming'
  profile: ChatProfile
  requestedAt?: string
  connectedAt?: string | null
  created?: boolean
}

export interface ChatSettings {
  enabled: boolean
  whoCanStart: ChatWhoCanStart
  startMode?: ChatStartMode
}

/** F-184: the person's own chat status (`/chat/me`). BUSY and AVAILABLE both mean `chatAvailable: true`. */
export type ChatStatus = 'AVAILABLE' | 'BUSY' | 'UNAVAILABLE'
/** Someone else's status: only while they are online (null otherwise). AWAY once the API sends it (automatic, idle). */
export type ChatPresenceStatus = 'AVAILABLE' | 'BUSY' | 'AWAY'
/** What my own dot shows: my chosen status, or AWAY while idle on top of AVAILABLE. */
export type ChatShownStatus = ChatStatus | 'AWAY'

export interface ChatMe {
  chatAvailable: boolean
  /** F-184; an older API leaves it out, and it is derived from `chatAvailable`. */
  chatStatus?: ChatStatus
  /** `false`: show the one-time "you are available" notice, then PATCH `availabilityNoticeSeen: true`. */
  availabilityNoticeSeen?: boolean
  startMode?: ChatStartMode
  chatReadReceipts: boolean
  /** F-184: show when I was last online (off: I do not see anyone's either). Absent on an older API. */
  chatShowLastSeen?: boolean
  panelState?: ChatPanelState | null
  canStart?: boolean
  cannotStartReason?: 'teamOnly' | 'paidOnly' | null
  whoCanStart?: ChatWhoCanStart
  unreadTotal?: number
  unreadConversations?: number
  showUnreadBadge?: boolean
}

export interface ChatProfile {
  id: string
  name?: string | null
  role?: string
  /** Owner, admin, moderator or finance (highlighted in the chat). */
  isTeam?: boolean | null
  tagline?: string | null
  jobTitle?: string | null
  avatar?: string | null
  /** F-184: AVAILABLE, AWAY or BUSY while online; null offline, Unavailable or blocked (the same on purpose). */
  status?: ChatPresenceStatus | null
  /** F-184: when they were last online, only when it may be shown (null otherwise). */
  lastSeenAt?: string | null
}

export interface ChatMessage {
  id: string
  conversationId: string
  senderId: string
  mine?: boolean
  deleted?: boolean
  body: string | null
  createdAt: string
  /** Only on the socket's `message.new`: the receiver is unavailable, so no alert. */
  silent?: boolean
}

export interface ChatConversation {
  id: string
  other: (ChatProfile & { available?: boolean; online?: boolean | null }) | null
  lastMessage?: ChatMessage | null
  lastMessageAt?: string | null
  unreadCount?: number
  lastReadAt?: string | null
  otherLastReadAt?: string | null
  readReceipts?: boolean
  canSend?: boolean
  /** `CONNECTION` mode without an accepted connection: read-only until both connect. */
  needsConnection?: boolean
  connection?: ChatConnectionState
  blockedByMe?: boolean
  created?: boolean
  createdAt?: string
}

export interface ChatPage<T> {
  items: T[]
  pagination?: { page?: number; totalPages?: number; total?: number; limit?: number; hasMore?: boolean; nextCursor?: string | null; realtime?: boolean }
}

/** One row of "Pessoas": an available person of the community and whether they are online. */
export interface ChatPerson {
  profile: ChatProfile
  online: boolean
  canStart?: boolean
  connection?: ChatConnectionState
  limited?: boolean
}

export interface ChatPeoplePage {
  items: ChatPerson[]
  nextCursor: string | null
  /** How many of them are online now (`pagination.online`), when the route says. */
  onlineTotal?: number
  /** People in each section for the current search (`pagination.teamTotal` / `membersTotal`), when the route says. */
  teamTotal?: number
  membersTotal?: number
}

export interface ChatBlock {
  profile: ChatProfile
  blockedAt?: string
}

/** Accepts both the array and the `{ items, pagination }` shapes. */
const asPage = <T>(result: unknown): ChatPage<T> => {
  if (Array.isArray(result)) return { items: result as T[] }
  const paged = (result || {}) as Partial<ChatPage<T>> & { data?: T[] }
  return { items: paged.items || paged.data || [], pagination: paged.pagination }
}

export class ChatResource {
  constructor(private ctx: SdkContext) {}

  /** Owner/admin; anyone else gets 403. */
  async getSettings(): Promise<ChatSettings> {
    return noteRowFrom('chatSettings', await this.ctx.call<ChatSettings>({ httpMethod: 'GET', path: '/chat/settings' }))
  }

  /** Sends the version read (`updatedAt`); a stale save (409) asks before saving over it. */
  async updateSettings(body: Partial<ChatSettings>): Promise<ChatSettings> {
    return versionedRowWrite('chatSettings', (v) => this.ctx.call<ChatSettings>({ httpMethod: 'PATCH', path: '/chat/settings', hasBody: true }, { body: { ...body, ...(v ? { updatedAt: v } : {}) } }))
  }

  /** 403 (`errors[0].param = enabled`) when the chat is off in the community. */
  async getMe(): Promise<ChatMe> {
    return this.ctx.call({ httpMethod: 'GET', path: '/chat/me' })
  }

  async updateMe(body: Partial<Pick<ChatMe, 'chatAvailable' | 'chatStatus' | 'chatReadReceipts' | 'chatShowLastSeen' | 'panelState' | 'availabilityNoticeSeen'>>): Promise<ChatMe> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/chat/me', hasBody: true }, { body })
  }

  /** Available and online now; 403 while the caller is unavailable. */
  async listOnline(query: { page?: number; limit?: number } = {}): Promise<ChatPage<ChatProfile>> {
    return asPage(await this.ctx.call({ httpMethod: 'GET', path: '/chat/online' }, { query }))
  }

  /**
   * "Pessoas": the available members, online first then by name (`GET /chat/members`, cursor + `q`);
   * 403 while the caller is unavailable. Rows are a flat profile plus `online`, `lastSeenAt`, `canStart`
   * (a nested `{ profile }` is accepted too). On 404 (an API without the route) it falls back to
   * `GET /chat/online`, filtered here by name.
   */
  async listPeople(query: { q?: string; cursor?: string | null; limit?: number } = {}): Promise<ChatPeoplePage> {
    const toPerson = (row: unknown, fallbackOnline: boolean): ChatPerson => {
      const r = (row || {}) as Partial<ChatPerson> & ChatProfile
      const base = r.profile || (r as ChatProfile)
      /* `role` / `isTeam` may come on the row or on the nested profile. */
      const profile: ChatProfile = { ...base, role: base.role ?? r.role, isTeam: base.isTeam ?? r.isTeam, status: base.status ?? r.status ?? null, lastSeenAt: base.lastSeenAt ?? (r as { lastSeenAt?: string | null }).lastSeenAt ?? null }
      return { profile, online: typeof r.online === 'boolean' ? r.online : fallbackOnline, canStart: r.canStart, connection: r.connection, limited: r.limited }
    }
    try {
      const params: Record<string, unknown> = { limit: query.limit || 30 }
      if (query.q) params.q = query.q
      if (query.cursor) params.cursor = query.cursor
      const page = asPage<unknown>(await this.ctx.call({ httpMethod: 'GET', path: '/chat/members' }, { query: params }))
      const pagination = (page.pagination || {}) as { nextCursor?: string | null; online?: number; realtime?: boolean; teamTotal?: number; membersTotal?: number }
      /* `realtime: false`: nobody's presence is known, so everyone reads offline. */
      const items = page.items.map((row) => toPerson(row, false)).map((p) => (pagination.realtime === false ? { ...p, online: false } : p))
      const num = (v: unknown) => (typeof v === 'number' ? v : undefined)
      return { items, nextCursor: pagination.nextCursor ?? null, onlineTotal: num(pagination.online), teamTotal: num(pagination.teamTotal), membersTotal: num(pagination.membersTotal) }
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status !== 404) throw err
      const online = await this.listOnline({ limit: 50 })
      const q = (query.q || '').trim().toLocaleLowerCase()
      const items = online.items.filter((p) => !q || (p.name || '').toLocaleLowerCase().includes(q)).map((p) => toPerson(p, true))
      return { items, nextCursor: null }
    }
  }

  async listConversations(query: { page?: number; limit?: number } = {}): Promise<ChatPage<ChatConversation>> {
    return asPage(await this.ctx.call({ httpMethod: 'GET', path: '/chat/conversations' }, { query }))
  }

  /** Opens a conversation with the profile, or returns the one that exists. */
  async openConversation(profileId: string): Promise<ChatConversation> {
    return this.ctx.call({ httpMethod: 'POST', path: '/chat/conversations', hasBody: true }, { body: { profileId } })
  }

  /**
   * "Fale com a equipe" (F-161b): opens (or returns) the direct conversation with whoever on the team
   * attends to a padlocked space or section. The server picks the person (`other`); nothing is sent.
   */
  async contactTeam(target: { spaceId: string } | { sectionId: string }): Promise<ChatConversation & { created?: boolean }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/chat/contact-team', hasBody: true }, { body: target })
  }

  async getConversation(id: string): Promise<ChatConversation> {
    return this.ctx.call({ httpMethod: 'GET', path: '/chat/conversations/{id}' }, { id })
  }

  /** One page in chronological order; `before` is the id of the oldest message already loaded. */
  async listMessages(id: string, query: { before?: string; limit?: number } = {}): Promise<ChatPage<ChatMessage>> {
    return asPage(await this.ctx.call({ httpMethod: 'GET', path: '/chat/conversations/{id}/messages' }, { id, query }))
  }

  async sendMessage(id: string, body: string): Promise<ChatMessage> {
    return this.ctx.call({ httpMethod: 'POST', path: '/chat/conversations/{id}/messages', hasBody: true }, { id, body: { body } })
  }

  /** "Limpar histórico": hides every message so far for the caller only; the other person keeps theirs. */
  async clearConversation(id: string): Promise<unknown> {
    return this.ctx.call({ httpMethod: 'POST', path: '/chat/conversations/{id}/clear', hasBody: true }, { id, body: {} })
  }

  async markRead(id: string): Promise<{ conversationId: string; lastReadAt: string }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/chat/conversations/{id}/read', hasBody: true }, { id, body: {} })
  }

  async deleteMessage(id: string): Promise<{ id: string; conversationId: string; deletedAt: string }> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/chat/messages/{id}' }, { id })
  }

  async listBlocks(): Promise<ChatPage<ChatBlock>> {
    return asPage(await this.ctx.call({ httpMethod: 'GET', path: '/chat/blocks' }))
  }

  async block(profileId: string): Promise<{ profileId: string; blocked: boolean; conversationId?: string | null }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/chat/blocks/{profileId}', hasBody: true }, { profileId, body: {} })
  }

  async unblock(profileId: string): Promise<{ profileId: string; blocked: boolean; conversationId?: string | null }> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/chat/blocks/{profileId}' }, { profileId })
  }

  /** Accepted connections. */
  async listConnections(query: { page?: number; limit?: number } = {}): Promise<ChatPage<ChatConnection>> {
    return asPage(await this.ctx.call({ httpMethod: 'GET', path: '/chat/connections' }, { query }))
  }

  /** Asks to connect; returns what already exists, and accepts when the other person had asked first. */
  async requestConnection(profileId: string): Promise<ChatConnection> {
    /* 429 `requestCooldown` (asking again within 24 h of a cancel): the error carries `retryAfter` (seconds). */
    return this.ctx.callKeepingRetryAfter({ httpMethod: 'POST', path: '/chat/connections', hasBody: true }, { body: { profileId } })
  }

  async listIncomingRequests(query: { page?: number; limit?: number } = {}): Promise<ChatPage<ChatConnection>> {
    return asPage(await this.ctx.call({ httpMethod: 'GET', path: '/chat/connections/requests/incoming' }, { query }))
  }

  async listOutgoingRequests(query: { page?: number; limit?: number } = {}): Promise<ChatPage<ChatConnection>> {
    return asPage(await this.ctx.call({ httpMethod: 'GET', path: '/chat/connections/requests/outgoing' }, { query }))
  }

  async acceptConnection(id: string): Promise<ChatConnection> {
    return this.ctx.call({ httpMethod: 'POST', path: '/chat/connections/{id}/accept', hasBody: true }, { id, body: {} })
  }

  /** Silent: the person who asked is not told. */
  async declineConnection(id: string): Promise<ChatConnection> {
    return this.ctx.call({ httpMethod: 'POST', path: '/chat/connections/{id}/decline', hasBody: true }, { id, body: {} })
  }

  /** Cancels my request or undoes a connection. */
  async removeConnection(id: string): Promise<unknown> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/chat/connections/{id}' }, { id })
  }
}
